import { useState } from "react";
import { useDispatch } from "react-redux";
import axiosInstance from "@API/axios";
import { setSnackbar } from "@Redux/snackBarSlice";

const GENERIC_ERROR = "Failed to export. Please try again.";
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Reads the filename the backend chose, so an export is named consistently wherever it is
 * triggered from. Falls back to a timestamped name when the header is not exposed.
 */
const resolveFileName = (headers, fileNamePrefix) => {
  const disposition = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  if (match) {
    return match[1];
  }
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
  return `${fileNamePrefix || "export"}-${stamp}.xlsx`;
};

/**
 * With responseType 'blob' an axios error body arrives as a Blob, so the backend's message is
 * invisible unless it is read back as text. Without this the row-cap 400 would surface as a
 * generic failure instead of telling the user to narrow their filters.
 *
 * GlobalAdviseController puts the real text in `details` and a category in `message`, so `details`
 * is preferred.
 */
const readErrorMessage = async (error) => {
  const body = error?.response?.data;
  if (!(body instanceof Blob)) {
    return body?.details || body?.message || GENERIC_ERROR;
  }
  try {
    const parsed = JSON.parse(await body.text());
    return parsed?.details || parsed?.message || GENERIC_ERROR;
  } catch (parseError) {
    return GENERIC_ERROR;
  }
};

const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Downloads a server-generated .xlsx for a listing, applying the filters and sort currently in
 * effect on the grid rather than only the rows on screen.
 */
const useExcelExport = () => {
  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  const exportToExcel = async ({
    endpoint,
    filters = [],
    columns = [],
    ids = null,
    sortBy = "id",
    sortDir = "asc",
    fileNamePrefix = "exit-requests",
  }) => {
    setExporting(true);
    try {
      const query = new URLSearchParams({ sortBy, sortDir });
      // `ids` is omitted entirely unless the caller filters client-side. An empty array is a
      // meaningful value there ("the filters matched nothing"), so it must not be conflated with
      // "no selection" — hence the null check rather than a length check.
      const body = ids === null ? { filters, columns } : { filters, columns, ids };
      const response = await axiosInstance.post(
        `${endpoint}?${query}`,
        body,
        { responseType: "blob" },
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: XLSX_MIME });

      if (blob.size === 0) {
        throw new Error("Empty export");
      }

      triggerDownload(blob, resolveFileName(response.headers, fileNamePrefix));
      return true;
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: await readErrorMessage(error),
          error: true,
        }),
      );
      return false;
    } finally {
      setExporting(false);
    }
  };

  return { exporting, exportToExcel };
};

export default useExcelExport;
